// Bespoke building prefabs: tiered fortifications and social buildings.
//
// Coordinate convention: origin at the building's base on the tile. Y up,
// X right, Z forward (toward camera). See buildings_extra.ts for the full
// group list; the shared `box()` helper and palette live in
// buildings_extra_shared.ts.

import { DARK_STONE, DARK_WOOD, STONE, WOOD, box } from './buildings_extra_shared.js';
import type { BlockVoxel, StructurePrefab } from './types.js';

// ── Fortifications (tiered) ────────────────────────────────────────────

export function wallPrefab(tier: number): StructurePrefab {
  const height = 2 + tier;
  const crenellations = tier >= 2;
  const blocks: BlockVoxel[] = [
    // Base
    ...box(-3, 0, -1, 7, 1, 3, DARK_STONE),
    // Wall body
    ...box(-3, 1, -1, 7, height, 3, STONE),
  ];
  if (crenellations) {
    for (let x = -3; x <= 3; x += 2) {
      blocks.push({ x, y: height + 1, z: -1, color: STONE });
      blocks.push({ x, y: height + 1, z: 0, color: STONE });
      blocks.push({ x, y: height + 1, z: 1, color: STONE });
    }
  }
  if (tier >= 3) {
    // Reinforcement iron bands
    blocks.push({ x: -3, y: 2, z: 0, color: '#444444' });
    blocks.push({ x: 0, y: 2, z: 0, color: '#444444' });
    blocks.push({ x: 3, y: 2, z: 0, color: '#444444' });
    blocks.push({ x: -3, y: height, z: 0, color: '#444444' });
    blocks.push({ x: 0, y: height, z: 0, color: '#444444' });
    blocks.push({ x: 3, y: height, z: 0, color: '#444444' });
  }
  return {
    name: tier === 1 ? 'Wooden Wall' : tier === 2 ? 'Stone Wall' : 'Ironbound Wall',
    blocks,
  };
}

export function watchTowerPrefab(tier: number): StructurePrefab {
  const height = 4 + tier * 2;
  const blocks: BlockVoxel[] = [
    // Foundation
    ...box(-1, 0, -1, 3, 1, 3, DARK_STONE),
    // Tower shaft
    ...box(-1, 1, -1, 3, height, 3, STONE),
    // Window slits
    { x: 0, y: Math.floor(height / 2), z: 1, color: '#1a1a1a' },
    { x: 0, y: Math.floor(height / 2) + 2, z: 1, color: '#1a1a1a' },
    // Platform crenellations
    { x: -2, y: height + 1, z: -2, color: STONE },
    { x: 0, y: height + 1, z: -2, color: STONE },
    { x: 2, y: height + 1, z: -2, color: STONE },
    { x: -2, y: height + 1, z: 2, color: STONE },
    { x: 0, y: height + 1, z: 2, color: STONE },
    { x: 2, y: height + 1, z: 2, color: STONE },
  ];
  if (tier >= 2) {
    // Pointed roof
    blocks.push({ x: 0, y: height + 2, z: 0, color: '#8b1a1a' });
    blocks.push({ x: -1, y: height + 2, z: 0, color: '#8b1a1a' });
    blocks.push({ x: 1, y: height + 2, z: 0, color: '#8b1a1a' });
    blocks.push({ x: 0, y: height + 3, z: 0, color: '#5a0a0a' });
  }
  if (tier >= 3) {
    // Warning beacon
    blocks.push({ x: 0, y: height + 2, z: 0, color: '#ff6622' });
  }
  return {
    name: tier === 1 ? 'Watchtower' : tier === 2 ? 'Guard Tower' : 'Wizard Tower',
    blocks,
  };
}

// ── Social (theater, colosseum) ────────────────────────────────────────

export const theater: StructurePrefab = {
  name: 'Theater',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Stage
    ...box(-2, 1, -2, 5, 1, 3, WOOD),
    // Stage backdrop
    ...box(-2, 2, -2, 5, 3, 1, '#7a1a1a'),
    // Curtain
    ...box(-1, 2, -1, 3, 3, 1, '#8b1a1a'),
    // Proscenium columns
    { x: -2, y: 2, z: 1, color: '#e0e0e0' },
    { x: -2, y: 3, z: 1, color: '#e0e0e0' },
    { x: -2, y: 4, z: 1, color: '#e0e0e0' },
    { x: 2, y: 2, z: 1, color: '#e0e0e0' },
    { x: 2, y: 3, z: 1, color: '#e0e0e0' },
    { x: 2, y: 4, z: 1, color: '#e0e0e0' },
    // Roof / marquee
    ...box(-2, 5, -2, 5, 1, 3, '#d4af37'),
    // Audience seating (semi-circular rows)
    { x: -1, y: 1, z: 2, color: '#5a3a1a' },
    { x: 0, y: 1, z: 2, color: '#5a3a1a' },
    { x: 1, y: 1, z: 2, color: '#5a3a1a' },
    { x: -2, y: 1, z: 1, color: '#5a3a1a' },
    { x: 2, y: 1, z: 1, color: '#5a3a1a' },
    // Marquee sign with gold trim
    { x: 0, y: 5, z: 1, color: '#ffcc44' },
  ],
};

export const colosseum: StructurePrefab = {
  name: 'Colosseum',
  blocks: [
    // Large oval foundation (approximated)
    ...box(-4, 0, -4, 9, 1, 9, DARK_STONE),
    // Outer ring wall (lower tier)
    ...box(-4, 1, -4, 9, 2, 1, STONE),
    ...box(-4, 1, 4, 9, 2, 1, STONE),
    ...box(-4, 1, -4, 1, 2, 9, STONE),
    ...box(4, 1, -4, 1, 2, 9, STONE),
    // Middle tier with arches
    ...box(-4, 3, -4, 9, 2, 1, STONE),
    ...box(-4, 3, 4, 9, 2, 1, STONE),
    ...box(-4, 3, -4, 1, 2, 9, STONE),
    ...box(4, 3, -4, 1, 2, 9, STONE),
    // Arch windows (dark)
    { x: -3, y: 4, z: -4, color: '#1a1a1a' },
    { x: 0, y: 4, z: -4, color: '#1a1a1a' },
    { x: 3, y: 4, z: -4, color: '#1a1a1a' },
    { x: -3, y: 4, z: 4, color: '#1a1a1a' },
    { x: 0, y: 4, z: 4, color: '#1a1a1a' },
    { x: 3, y: 4, z: 4, color: '#1a1a1a' },
    // Upper tier
    ...box(-4, 5, -4, 9, 1, 1, STONE),
    ...box(-4, 5, 4, 9, 1, 1, STONE),
    ...box(-4, 5, -4, 1, 1, 9, STONE),
    ...box(4, 5, -4, 1, 1, 9, STONE),
    // Arena floor (sand)
    ...box(-3, 1, -3, 7, 1, 7, '#d4a04a'),
    // Gladiator gate
    { x: 0, y: 1, z: 4, color: DARK_WOOD },
    { x: 0, y: 2, z: 4, color: DARK_WOOD },
    // Imperial box
    ...box(-1, 6, -4, 3, 1, 1, '#d4af37'),
  ],
};
